import { ChatCircleIcon } from "@phosphor-icons/react/dist/csr/ChatCircle";
import { type FormEvent, useId, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { Textarea } from "@volca/ui/components/ui/textarea";
import { profitabilityTemplateEditPath } from "@/profitability/routes";
import type { EditAgentState } from "@/profitability/template-page/use-agent-nav";

/** What the template conversation is sent for a check asked for here. */
export const checkQuestion = (request: string) =>
  `Add a check to this agent: ${request.trim()}`;

/**
 * "Ask for a check": the owner says in their own words what Volca should
 * look out for each period, and Edit agent (the agent's conversation)
 * takes it from there. Volca turns it into a rule with a threshold they can
 * change and builds it as a draft to review and publish, or says what the
 * files are missing and lists it here as not checked yet.
 */
export const AskCheck = ({ reportId }: { reportId: string }) => {
  const navigate = useNavigate();
  const id = useId();
  const [draft, setDraft] = useState("");
  const send = (event: FormEvent) => {
    event.preventDefault();
    if (!draft.trim()) return;
    const state: EditAgentState = { request: checkQuestion(draft) };
    void navigate(profitabilityTemplateEditPath(reportId), { state });
  };
  return (
    <form onSubmit={send} className="px-4 py-4">
      <Stack gap={2} align="stretch">
        <Stack gap={0.5} align="start">
          <Text variant="label" render={<label htmlFor={id} />}>
            Ask for a check
          </Text>
          <Text variant="caption" tone="muted" className="text-pretty">
            Say what Volca should look out for each period. It turns it into a
            rule you can tune, and builds it for you to review.
          </Text>
        </Stack>
        <Textarea
          id={id}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && (event.metaKey || event.ctrlKey))
              send(event);
          }}
          placeholder="Flag install jobs where crew hours ran over the estimate"
          rows={2}
          className="min-h-14"
        />
        <Stack direction="row" justify="end">
          <Button type="submit" variant="secondary" disabled={!draft.trim()}>
            <ChatCircleIcon aria-hidden />
            Ask Volca
          </Button>
        </Stack>
      </Stack>
    </form>
  );
};
